import './styles/Article.scss';

import { useApolloClient, useMutation } from '@apollo/client';
import { Grid } from '@mui/material';

import { Formik } from 'formik';
import { Gateway } from 'index';
import { assign, isEmpty, omit, omitBy, pick } from 'lodash';
import omitDeep from 'omit-deep-lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import CREATE_MUTATION from 'gql/mutations/addArticle.gql';
import UPDATE_MUTATION from 'gql/mutations/updateArticle.gql';
import USER_QUERY from 'gql/queries/currentUser.gql';
import QUERY from 'gql/queries/getArticle.gql';

import MultistepForm from 'app/components/MultistepForm';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import type { Article as ArticleInterface } from 'types/AppTypes';
import { ACLHelper } from 'utils/ACLHelper';
import { validateForm } from 'utils/FormErrorHelper';

import ArticleType from './components/ArticleType';
import Lifetime from './components/Lifetime';
import MainContent from './components/MainContent';
import Preview from './components/Preview';
import { UserGroupNew } from './components/UserGroup';
import { articleInitialValues, articleValidationSchema } from './validation';

interface Props {
  onCancel: Function;
  closeHandler: Function;
  article?: ArticleInterface;
  setDisabledCancel?: Function;
}

export default function Article(props: Props) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();
  const [createArticle] = useMutation(CREATE_MUTATION);
  const [updateArticle] = useMutation(UPDATE_MUTATION);
  const client = useApolloClient();
  const data = client.readQuery({ query: USER_QUERY });
  const aclHelper = new ACLHelper(data.currentUser);

  const steps = [
    t(translations.pages.articles.steps.userGroup()),
    t(translations.pages.articles.steps.category()),
    t(translations.pages.articles.steps.mainContent()),
    t(translations.pages.articles.steps.lifetime()),
    t(translations.pages.articles.steps.review())
  ];

  const handleSaveClick = async values => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    if (props.setDisabledCancel) {
      props.setDisabledCancel(true);
    }
    const omitFields = [
      '__typename',
      'id',
      'postAs',
      'recipients',
      'articleText',
      'attachement',
      'withAppointment',
      'isSuperUser'
    ];
    if (
      values.type !== 'event' &&
      values.type !== 'legal' &&
      values.type !== 'holiday' &&
      !values.withAppointment
    ) {
      omitFields.push('appointment');
    }
    let saveBody: any = {
      ...omit(values, omitFields),
      articleText: values.articleText.replace(/<p><br><\/p>/g, ''),
      author: !isEmpty(values.author)
        ? pick(values.author, ['id', 'email', 'firstName', 'lastName'])
        : pick(data.currentUser, ['uid', 'email', 'firstName', 'lastName']),
      recipients: values.recipients,
      semesters: values.recipients.find(recipient => recipient.includes('Studenten'))
        ? values.semesters
        : []
    };

    if (saveBody.author.uid) {
      saveBody.author.id = saveBody.author.uid;
      delete saveBody.author.uid;
    }
    assign(saveBody.author, { postAs: values.postAs });

    if (values.attachement) {
      // Save logo first
      const formData = new FormData();
      formData.append('file', values.attachement);
      const { data: fileURI } = await Gateway.post('/upload/article/file', formData);
      saveBody.attachement = fileURI;
    }

    if (values.id) {
      //* UPDATE
      if (!values.withAppointment) {
        saveBody.appointment = null;
      }
      try {
        const sanitizedSaveBody = omitDeep(saveBody, '__typename');
        await updateArticle({
          variables: {
            id: values.id,
            data: omitBy(sanitizedSaveBody, v => v === null || v === '')
          },
          refetchQueries: ['GetArticles', { query: QUERY, variables: { id: values.id } }]
        });
        props.closeHandler();
        dispatch(AlertActions.success(t(translations.pages.articles.alerts.saveSuccessful())));
      } catch (err) {
        dispatch(AlertActions.error(t(translations.pages.articles.alerts.saveFailed())));
      }
    } else {
      //* CREATE
      try {
        await createArticle({
          variables: {
            data: omitBy(saveBody, v => v === null || v === '')
          },
          refetchQueries: ['GetArticles']
        });
        dispatch(AlertActions.success(t(translations.pages.articles.alerts.saveSuccessful())));
        props.closeHandler();
      } catch (err) {
        dispatch(AlertActions.error(t(translations.pages.articles.alerts.saveFailed())));
      }
    }
    setSubmitting(false);
    if (props.setDisabledCancel) {
      props.setDisabledCancel(false);
    }
  };

  const ArticleStep = ({ step }) => {
    switch (step) {
      case 0:
        return <UserGroupNew onClose={props.closeHandler} />;
      case 1:
        return <ArticleType />;
      case 2:
        return <MainContent />;
      case 3:
        return <Lifetime />;
      case 4:
        return <Preview />;
      default:
        return null;
    }
  };

  return (
    <Grid item className="articleModal">
      <Formik
        isInitialValid={!!props.article}
        initialValues={articleInitialValues(props.article, aclHelper.isSuperUser())}
        onSubmit={values => {
          handleSaveClick(values);
        }}
        validate={values => {
          return validateForm(values, yup.object().shape(articleValidationSchema(t)), dispatch);
        }}
      >
        <MultistepForm
          submitting={submitting}
          onCancel={props.onCancel}
          steps={steps}
          // TODO: add step validation
          stepValidator={() => true}
        >
          {activeStep => (
            <Grid container className="articleModal__body">
              <ArticleStep step={activeStep} />
            </Grid>
          )}
        </MultistepForm>
      </Formik>
    </Grid>
  );
}
